/**
 * LLMToggle.jsx — Dynamischer LLM-Provider-Dropdown
 * CLARA360-DYNAMIC-LLM-PROVIDER-CATALOG-1
 *
 * - Lädt Modelle aus /api/llm/available-models (org-scoped)
 * - Keine hardcodierten Modellnamen
 * - 3 Zustände: available (auswählbar), degraded (Warnung), offline (nicht auswählbar)
 * - Fallback auf Atlas Auto wenn Endpoint ausfällt oder Modell verschwindet
 * - Gruppierung: Auto | Lokal | Cloud
 */

import React, { useState, useEffect, useCallback, useRef } from 'react';
import { Brain, Zap, Sparkles, ChevronDown, Settings, AlertTriangle, WifiOff, Eye } from 'lucide-react';
import { useAuth } from '../contexts/AuthContext';
import { masterSupabase as supabase } from '../lib/supabase-master.js';

// Capability-Icons für Modell-Badges
const CAPABILITY_ICONS = {
  vision: '👁',
  vision_high_accuracy: '🔬',
  document_parse: '📄',
  ocr_reasoning: '📄',
  structured_extraction: '🗂',
  reasoning: '🧠',
  tool_use: '🔧',
  fast_classification: '⚡',
  chat: null, // kein Badge für Standard-Chat
};

function getCapabilityBadges(capabilities = []) {
  return capabilities
    .map((c) => CAPABILITY_ICONS[c])
    .filter(Boolean)
    .slice(0, 2);
}

// Provider-Farben
const PROVIDER_COLORS = {
  ollama_local: { active: 'bg-orange-600 shadow-orange-600/25', hover: 'hover:text-orange-400', text: 'text-orange-400' },
  gemini: { active: 'bg-blue-600 shadow-blue-600/25', hover: 'hover:text-blue-400', text: 'text-blue-400' },
  nim: { active: 'bg-green-600 shadow-green-600/25', hover: 'hover:text-green-400', text: 'text-green-400' },
  clara_hosted: { active: 'bg-violet-600 shadow-violet-600/25', hover: 'hover:text-violet-400', text: 'text-violet-400' },
  clara_demo: { active: 'bg-gray-600 shadow-gray-600/25', hover: 'hover:text-gray-400', text: 'text-gray-400' },
  default: { active: 'bg-orange-600 shadow-orange-600/25', hover: 'hover:text-orange-400', text: 'text-orange-400' },
};

const FALLBACK_CATALOG = {
  groups: {
    auto: [{ id: 'clara-auto', label: 'Atlas Auto', capabilities: ['chat'], availability: 'available', provider_type: 'clara_hosted', is_default: true }],
    local: [],
    cloud: [],
  },
};

const LLMToggle = ({ selectedModel, onModelChange }) => {
  const { isSuperAdmin, isLoading: authLoading, isAuthenticated } = useAuth();
  const [showDropdown, setShowDropdown] = useState(false);
  const [catalog, setCatalog] = useState(FALLBACK_CATALOG);
  const [loading, setLoading] = useState(true);
  const [endpointError, setEndpointError] = useState(false);
  const dropdownRef = useRef(null);

  // ─────────────────────────────────────────────────────────────
  // Modellkatalog laden
  // ─────────────────────────────────────────────────────────────
  const loadCatalog = useCallback(async () => {
    try {
      const { data: { session } } = await supabase.auth.getSession();
      if (!session?.access_token) {
        // Session noch nicht vorhanden — kurz warten und nochmal versuchen
        // (Race Condition: AuthContext noch am Initialisieren)
        if (!authLoading) {
          console.warn('[LLMToggle] No session available, using fallback catalog');
          setCatalog(FALLBACK_CATALOG);
          setEndpointError(true);
        }
        setLoading(false);
        return;
      }

      const resp = await fetch('/api/llm/available-models', {
        headers: {
          'x-supabase-access-token': session.access_token,
        },
      });

      if (!resp.ok) throw new Error(`HTTP ${resp.status}`);
      const data = await resp.json();
      setCatalog(data);
      setEndpointError(false);

      // Fallback: wenn aktuell gewähltes Modell nicht mehr verfügbar
      const allModels = [
        ...(data.groups?.auto || []),
        ...(data.groups?.local || []),
        ...(data.groups?.cloud || []),
      ];
      const currentStillAvailable = allModels.find(
        (m) => m.id === selectedModel && m.availability !== 'offline'
      );
      if (!currentStillAvailable && selectedModel !== 'clara-auto') {
        onModelChange('clara-auto');
      }
    } catch (e) {
      console.warn('[LLMToggle] Catalog load failed:', e.message);
      setEndpointError(true);
      setCatalog(FALLBACK_CATALOG);
      // Preserve an explicit user selection during transient catalogue failures.
      // The next successful refresh validates availability again.
    } finally {
      setLoading(false);
    }
  }, [selectedModel, onModelChange]);

  useEffect(() => {
    // Erst laden wenn AuthContext fertig initialisiert ist
    if (authLoading) return;
    loadCatalog();
    // Alle 60 Sekunden neu laden (entspricht Cache-TTL)
    const interval = setInterval(loadCatalog, 60 * 1000);
    return () => clearInterval(interval);
  }, [authLoading, loadCatalog]);

  // Click outside schließt Dropdown
  useEffect(() => {
    const handler = (e) => {
      if (dropdownRef.current && !dropdownRef.current.contains(e.target)) {
        setShowDropdown(false);
      }
    };
    document.addEventListener('mousedown', handler);
    return () => document.removeEventListener('mousedown', handler);
  }, []);

  // ─────────────────────────────────────────────────────────────
  // Aktuell gewähltes Modell finden
  // ─────────────────────────────────────────────────────────────
  const allModels = [
    ...(catalog.groups?.auto || []),
    ...(catalog.groups?.local || []),
    ...(catalog.groups?.cloud || []),
  ];

  const currentModel = allModels.find((m) => m.id === selectedModel)
    || allModels.find((m) => m.is_default)
    || { id: 'clara-auto', label: 'Atlas Auto', provider_type: 'clara_hosted', availability: 'available' };

  const providerColor = PROVIDER_COLORS[currentModel.provider_type] || PROVIDER_COLORS.default;

  // ─────────────────────────────────────────────────────────────
  // Modell-Button Rendering
  // ─────────────────────────────────────────────────────────────
  const renderModelButton = (model) => {
    const isSelected = selectedModel === model.id;
    const isOffline = model.availability === 'offline';
    const isDegraded = model.availability === 'degraded';
    const badges = getCapabilityBadges(model.capabilities);

    return (
      <button
        key={model.id}
        onClick={() => {
          if (!isOffline) {
            onModelChange(model.id);
            setShowDropdown(false);
          }
        }}
        disabled={isOffline}
        className={`w-full text-left px-3 py-2 text-xs transition-colors
          ${isSelected ? 'bg-orange-900/30 text-orange-300' : 'text-gray-300'}
          ${isOffline ? 'opacity-40 cursor-not-allowed' : 'hover:bg-gray-800'}
        `}
      >
        <div className="flex items-center justify-between">
          <div className="flex items-center gap-1 font-medium">
            {badges.length > 0 && <span>{badges.join('')}</span>}
            <span>{model.label}</span>
          </div>
          <div className="flex items-center gap-1">
            {isDegraded && <AlertTriangle className="w-3 h-3 text-yellow-500" />}
            {isOffline && <WifiOff className="w-3 h-3 text-red-500" />}
          </div>
        </div>
        {isDegraded && (
          <div className="text-[10px] text-yellow-500/70 mt-0.5">Eingeschränkt verfügbar</div>
        )}
        {isOffline && (
          <div className="text-[10px] text-red-500/70 mt-0.5">Nicht verfügbar</div>
        )}
      </button>
    );
  };

  const hasLocalModels = (catalog.groups?.local || []).length > 0;
  const hasCloudModels = (catalog.groups?.cloud || []).length > 0;

  // ─────────────────────────────────────────────────────────────
  // Render
  // ─────────────────────────────────────────────────────────────
  return (
    <div className="flex items-center space-x-2 bg-gray-800/50 rounded-lg p-2 border border-gray-600/30" ref={dropdownRef}>
      <span className="text-xs text-gray-400 font-medium">KI-Modell:</span>

      <div className="relative">
        {/* Haupt-Button: zeigt aktuelles Modell */}
        <button
          onClick={() => setShowDropdown(!showDropdown)}
          className={`flex items-center space-x-1 px-3 py-1 rounded text-xs font-medium transition-all duration-200 shadow-lg
            ${showDropdown || true ? `bg-gray-900/50` : ''}
            ${providerColor.text}
          `}
          title={endpointError ? 'Modellkatalog nicht verfügbar — Atlas Auto aktiv' : 'KI-Modell wählen'}
        >
          <Zap className="w-3 h-3" />
          <span>{loading ? '...' : currentModel.label}</span>
          {endpointError && <AlertTriangle className="w-3 h-3 text-yellow-500 ml-1" />}
          <ChevronDown className={`w-3 h-3 ml-0.5 transition-transform ${showDropdown ? 'rotate-180' : ''}`} />
        </button>

        {/* Dropdown */}
        {showDropdown && (
          <div className="absolute top-full left-0 mt-1 w-64 bg-gray-900 border border-gray-700 rounded-lg shadow-xl z-50 overflow-hidden">

            {/* Header */}
            <div className="px-3 py-2 border-b border-gray-700">
              <div className="flex items-center space-x-1 text-[10px] text-gray-500 uppercase tracking-wider">
                <Settings className="w-3 h-3" />
                <span>{isSuperAdmin ? 'Admin — Modellwahl' : 'Modell wählen'}</span>
              </div>
            </div>

            {/* Auto-Gruppe */}
            {(catalog.groups?.auto || []).length > 0 && (
              <>
                <div className="px-3 py-1 text-[10px] text-gray-600 uppercase tracking-wider bg-gray-900/80">Auto</div>
                {(catalog.groups.auto).map(renderModelButton)}
              </>
            )}

            {/* Lokal-Gruppe (nur wenn lokale Provider vorhanden) */}
            {hasLocalModels && (
              <>
                <div className="px-3 py-1 text-[10px] text-gray-600 uppercase tracking-wider bg-gray-900/80 border-t border-gray-800">Lokal</div>
                {(catalog.groups.local).map(renderModelButton)}
              </>
            )}

            {/* Cloud-Gruppe */}
            {hasCloudModels && (
              <>
                <div className="px-3 py-1 text-[10px] text-gray-600 uppercase tracking-wider bg-gray-900/80 border-t border-gray-800">Cloud</div>
                {(catalog.groups.cloud).map(renderModelButton)}
              </>
            )}

            {/* Fehler-Hinweis */}
            {endpointError && (
              <div className="px-3 py-2 border-t border-gray-700 text-[10px] text-yellow-500/70 flex items-center gap-1">
                <AlertTriangle className="w-3 h-3" />
                Katalog nicht erreichbar — Fallback aktiv
              </div>
            )}
          </div>
        )}
      </div>
    </div>
  );
};

export default LLMToggle;
